import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { useProfileStore } from '../../store/useProfileStore';

interface ErrorModalProps {
  isOpen: boolean;
  errorMessage: string;
  correlationId?: string;
  onClose: () => void;
}

function formatErrorMessage(message: string): string {
  // Extract status code if present
  const statusMatch = message.match(/^API Error (\d+):\s*/);
  const statusCode = statusMatch ? statusMatch[1] : null;
  const body = statusMatch ? message.slice(statusMatch[0].length) : message;

  // Try to parse as JSON and pretty print
  try {
    const parsed = JSON.parse(body);
    const lines: string[] = [];

    if (statusCode) {
      lines.push(`Status: ${statusCode}\n`);
    }

    // Pretty print the entire JSON response and unescape quotes
    const prettyJson = JSON.stringify(parsed, null, 2)
      .replace(/\\"/g, '"')
      .replace(/\\u003c/g, '<')
      .replace(/\\u003e/g, '>');
    lines.push(prettyJson);

    return lines.join('');
  } catch {
    // Not JSON, return as-is with status extracted
    if (statusCode) {
      return `Status: ${statusCode}\n\n${body}`;
    }
    return message;
  }
}

export function ErrorModal({ isOpen, errorMessage, correlationId, onClose }: ErrorModalProps) {
  const formattedMessage = formatErrorMessage(errorMessage);
  const isNonProd = import.meta.env.VITE_ENV !== 'prod';
  const { activeProfile } = useProfileStore();

  const onRequestSupport = () => {
    const baseUrl = 'https://40bddu.share-na2.hsforms.com/2t-kNBVHeTp-_hgZZKKJlPg';
    const params = new URLSearchParams();

    if (correlationId) {
      params.append('correlationId', correlationId);
    }

    if (activeProfile?.email) {
      params.append('email', activeProfile.email);
    }

    const url = `${baseUrl}?${params.toString()}`;
    window.open(url, '_blank');
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Error</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={close} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="flex flex-col items-center text-center py-8">
          <h1 className="text-2xl font-bold mb-2">Oops... something went wrong.</h1>
          <p className="text-gray-600 mb-2">Please try again or contact support.</p>
          <div className="flex justify-center my-6">
            <img
              src="https://storage.googleapis.com/rev-public/error.jpeg"
              alt="Error"
              className="max-w-full h-auto"
            />
          </div>
          <div>
            <p className="text-gray-800 mb-6">We're here to help. If your issue persists, click here to request support.</p>
            <IonButton onClick={onRequestSupport} color="secondary">
              Request Support
            </IonButton>
          </div>
          {isNonProd && (
            <div className="bg-gray-100 p-4 rounded-lg text-left text-sm max-w-full w-full whitespace-pre-wrap break-words font-mono">
              {formattedMessage}
            </div>
          )}
        </div>
      </IonContent>
      <div className="px-4 py-3 border-t flex flex-col items-center justify-center gap-2">
        {correlationId && (
          <p className="text-xs text-gray-500 font-mono">
            Correlation ID: {correlationId}
          </p>
        )}
        <IonButton onClick={onClose} color="primary">
          Close
        </IonButton>
      </div>
    </IonModal>
  );
}
